import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

// Cores do tema - Harmônico
const COLORS = {
  primary: '#d4a574',    // Rose gold / âmbar rosado
  gold: '#d4af37',       // Dourado
  blue: '#5b8fb9',       // Azul suave
  green: '#6b9b7a',      // Verde oliva
  red: '#c67b7b',        // Vermelho rosado
  orange: '#d4a574',     // Âmbar
  cyan: '#7ba3a8',       // Ciano suave
  purple: '#a08090',     // Roxo acinzentado
  slate: '#8b8585'       // Cinza rosado
};

const CHART_COLORS = [COLORS.primary, COLORS.gold, COLORS.blue, COLORS.green, COLORS.purple, COLORS.cyan];

// Tooltip customizado
const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#2a2020]/95 border border-amber-500/20 rounded-lg px-3 py-2 shadow-lg">
        <p className="text-amber-300 text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {valuePrefix}{typeof entry.value === 'number' ? entry.value.toLocaleString('pt-BR') : entry.value}{valueSuffix}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Gráfico de Barras
export const SimpleBarChart = ({ 
  data, 
  dataKey, 
  nameKey = 'name', 
  color = COLORS.purple,
  height = 300,
  valuePrefix = '',
  valueSuffix = ''
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis 
        dataKey={nameKey} 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <YAxis 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <Tooltip content={<CustomTooltip valuePrefix={valuePrefix} valueSuffix={valueSuffix} />} />
      <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

// Gráfico de Barras Empilhadas
export const StackedBarChart = ({ 
  data, 
  bars, // [{ dataKey: 'value1', name: 'Label 1', color: '#xxx' }]
  nameKey = 'name',
  height = 300
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis 
        dataKey={nameKey} 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <YAxis 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <Tooltip content={<CustomTooltip />} />
      <Legend 
        wrapperStyle={{ paddingTop: '10px' }}
        formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
      />
      {bars.map((bar, index) => (
        <Bar 
          key={bar.dataKey} 
          dataKey={bar.dataKey} 
          name={bar.name}
          stackId="a"
          fill={bar.color || CHART_COLORS[index % CHART_COLORS.length]} 
          radius={index === bars.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
        />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

// Gráfico de Pizza/Donut
export const DonutChart = ({ 
  data, // [{ name: 'Item', value: 100 }]
  height = 250,
  innerRadius = 50,
  outerRadius = 80,
  showLabels = true
}) => {
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    if (percent < 0.05) return null;
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="#9ca3af" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={11}
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={2}
          dataKey="value"
          label={showLabels ? renderCustomizedLabel : false}
          labelLine={showLabels}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Gráfico de Linha
export const SimpleLineChart = ({
  data,
  lines, // [{ dataKey: 'value', name: 'Label', color: '#xxx' }]
  nameKey = 'name',
  height = 300,
  valuePrefix = '',
  valueSuffix = ''
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis 
        dataKey={nameKey} 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <YAxis 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <Tooltip content={<CustomTooltip valuePrefix={valuePrefix} valueSuffix={valueSuffix} />} />
      <Legend 
        wrapperStyle={{ paddingTop: '10px' }}
        formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
      />
      {lines.map((line, index) => (
        <Line
          key={line.dataKey}
          type="monotone"
          dataKey={line.dataKey}
          name={line.name}
          stroke={line.color || CHART_COLORS[index % CHART_COLORS.length]}
          strokeWidth={2}
          dot={{ r: 4, fill: line.color || CHART_COLORS[index % CHART_COLORS.length] }}
          activeDot={{ r: 6 }}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

// Gráfico de Área
export const SimpleAreaChart = ({
  data,
  areas, // [{ dataKey: 'value', name: 'Label', color: '#xxx' }]
  nameKey = 'name',
  height = 300
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <defs>
        {areas.map((area, index) => (
          <linearGradient key={`gradient-${area.dataKey}`} id={`gradient-${area.dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={area.color || CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.4}/>
            <stop offset="95%" stopColor={area.color || CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0}/>
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis 
        dataKey={nameKey} 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <YAxis 
        tick={{ fill: '#9ca3af', fontSize: 11 }} 
        axisLine={{ stroke: '#4b5563' }}
      />
      <Tooltip content={<CustomTooltip />} />
      <Legend 
        wrapperStyle={{ paddingTop: '10px' }}
        formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
      />
      {areas.map((area, index) => (
        <Area
          key={area.dataKey}
          type="monotone"
          dataKey={area.dataKey}
          name={area.name}
          stroke={area.color || CHART_COLORS[index % CHART_COLORS.length]}
          fill={`url(#gradient-${area.dataKey})`}
          strokeWidth={2}
        />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);

// Micro gráfico de barras para cards
export const MiniBarChart = ({ data, dataKey, color = COLORS.purple, height = 60 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data}>
      <Bar dataKey={dataKey} fill={color} radius={[2, 2, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

// Exportar cores para uso externo
export { COLORS, CHART_COLORS };
